<template>
  <div v-if="(teamMembers ?? []).length > 0" class="etx-container Container" :class="paragraphClassList">
    <div class="etx-center Center">

      <div class="etx-flex Flex">
        <div class="etx-flex__item Flex__item mob-m:c-9 mob-l:c-12">

          <div :class="{ Tabs: (teamMembers ?? []).length > 1 }">

            <div v-if="(teamMembers ?? []).length > 1" class="Tabs__header" :class="entranceClassList">

              <div v-for="(teamMemberGroup, index) in teamMembers" :key="teamMemberGroup.id" class="Tabs__header__item" :class="{ active: activeTab === index }" @click="activeTab = index" :data-tab-id="'team-members-tab-' + teamMemberGroup.id">
                <span>{{ teamMemberGroup.label }}</span>
              </div>

            </div>

            <div :class="{ Tabs__content: (teamMembers ?? []).length > 1 }">

              <div v-for="(teamMemberGroup, index) in teamMembers"
                :key="teamMemberGroup.id"
                :class="{
                  Tabs__content__item: (teamMembers ?? []).length > 1,
                  active: (teamMembers ?? []).length > 1 && activeTab === index
                }"
                :data-tab-id="'team-members-tab-' + teamMemberGroup.id"
              >

                <div class="etx-flex Flex mob-l:c-2 tab-l:c-3 gh-200 gv-300">

                  <div v-for="(teamMember) in teamMemberGroup.members" class="etx-flex__item Flex__item">


                    <div class="etx-card Card" :class="entranceClassList">

                      <div class="etx-card__item Card__item ph-0 pv-0">

                        <div class="etx-media Media">
                          <MediaImage :media="teamMember?.image" imageStyle="image_1_1" />
                        </div>

                      </div>

                      <div class="etx-card__item Card__item ph-0 pt-75 pb-0">

                        <div class="etx-text Text">
                          <p class="font-size-larger font-weight-bold">{{ teamMember.name }} <span class="font-uppercase">{{ teamMember.surname }}</span></p>
                          <p class="font-weight-bold font-color-primary">{{ teamMember?.jobTitle?.label }}</p>
                        </div>

                        <div v-if="teamMember.phone1 || teamMember.email1" class="etx-accordion-list AccordionList AccordionList__ContactInfo">
                          <div class="etx-accordion Accordion">
                            <div class="etx-accordion__header Accordion__header">

                              <div class="etx-accordion__header__button Accordion__header__button" data-accordion-clickable>
                                <i class="far fa-plus font-color-secondary"></i>
                              </div>
                            </div>
                            <div class="etx-accordion__content Accordion__content">
                              <div class="etx-item-list ItemList ">

                                <a v-if="teamMember.phone1" class="etx-item Item" :href="`tel:${ teamMember.phone1 }`" title="">
                                  <div class="etx-item__icon Item__icon"><i class="fal fa-phone"></i></div>
                                  <div class="etx-item__text Item__text font-weight-bold"><span>{{ formatSwissPhoneNumber(teamMember.phone1) }}</span></div>
                                </a>

                                <a v-if="teamMember.email1" class="etx-item Item" :href="`mailto:${ teamMember.email1 }`" title="">
                                  <div class="etx-item__icon Item__icon"><i class="fal fa-envelope"></i></div>
                                  <div class="etx-item__text Item__text font-weight-bold"><span>{{ teamMember.email1 }}</span></div>
                                </a>

                              </div>
                            </div>
                          </div>
                        </div>

                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>
      </div>

    </div>
  </div>

  <div class="etx-container Container pt-50 pb-50" :class="paragraphClassList" v-else-if="isEditing">
    <div class="etx-center Center">
      <p class="editor-placeholder">
        Derzeit sind keine Mitarbeitende für die ausgewählten Kategorien verfügbar.
      </p>
    </div>
  </div>

</template>

<script lang="ts" setup>

const props = defineProps<{
  teamCategories: Array<{
    id: string,
    label: string
  }>,
  teamMembers: Array<{
    name: string,
    surname: string,
    jobTitle: {
      label: string,
      id: string
    },
    phone1: string,
    email1: string,
    image: {},
    categories: Array<{
      id: string,
      label: string
    }>
  }>
}>()

// Fetch the team members, with matching query key (for caching).
const categories: string[] = props.teamCategories.map((category) => category.id);
const queryKey = `teamMembers-${categories.join('-')}`;
const { data: teamMembers = [] } = await useAsyncData(queryKey, async () => {
  return await useGraphqlQuery('teamMembers', { 'categories': categories }).then((v) => {
    const results = v.data?.entityQuery?.items ?? []
    const sortedResults = sortTeamMembersNull(results)
    return groupItemsByCategory(sortedResults, categories)
  })
})

// Helper function grouping the results by terms, with labels and filtered by
// the available categories from the paragraphs.
function groupItemsByCategory(items: any[], availableCategories: string[]) {
  const grouped: { [key: string]: { label: string, members: any[] } } = {};
  items.forEach(item => {
    item.categories.forEach((category: any) => {
      if (availableCategories.includes(category.id)) {
        if (!grouped[category.id]) {
          grouped[category.id] = {
            label: category.label,
            members: []
          };
        }
        grouped[category.id].members.push(item);
      }
    });
  });

  return Object.keys(grouped).map(key => ({
    id: key,
    label: grouped[key].label,
    members: grouped[key].members
  }));
}

const getFontawesomeClass = (icon: string): string => {
  return 'fa-' + icon;
};

const activeTab = ref(0);

const { options, isEditing } = defineBlokkli({
  bundle: 'team_listing_categories',
  globalOptions: ['spacingTop', 'spacingBottom', 'backgroundColor'],
})

const paragraphClassList = computed(() => {

  const classList = []

  if (options.value.spacingTop) {
    classList.push(options.value.spacingTop)
  }

  if (options.value.spacingBottom) {
    classList.push(options.value.spacingBottom)
  }

  if (options.value.backgroundColor !== 'white') {
    classList.push(options.value.backgroundColor)
  }

  return classList
})

const entranceClassList = computed(() => {
  const classList = []
  if (!isEditing) {
    classList.push('will-appear', 'move-up')
  }
  return classList
})

</script>
